import {
  useQuery,
  UseQueryResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef, useEffect } from "react";
import { leadListsApi } from "../../../libs/landvoice-api";
import { LookupResponse } from "../../../libs/landvoice-api/client/api";
import { SavedSearchLookupStatusCode } from "../constants";
import { isNullOrUndefined } from "src/utils/objects";
import { EnumProductTypes } from "../../../types/listing";
import {
  getNsSavedSearchStatusCacheKey,
  getNsSavedSearchCacheKey,
  getNsSavedSearchDetailsCacheKey,
  NS_LIST_SAVED_SEARCHES_CACHE_KEY,
  NS_INFINITE_LIST_SAVED_SEARCHES_CACHE_KEY,
} from "../constants/cacheKeys";

export const useFetchSavedSearchStatus = (
  savedSearchId: string,
  enabled = true
): UseQueryResult<LookupResponse> => {
  const queryClient = useQueryClient();
  const previousStatusRef = useRef<string | undefined>();

  const result = useQuery({
    queryKey: getNsSavedSearchStatusCacheKey(savedSearchId),
    queryFn: async () => {
      const response = await leadListsApi.getJobStatus(savedSearchId);
      return response?.data;
    },
    enabled: !!savedSearchId && enabled,
    refetchInterval: (data) => {
      // Only refetch if status is still in progress or queued
      const status = data?.state?.data?.lookup_status;
      if (
        status === SavedSearchLookupStatusCode.Queued ||
        status === SavedSearchLookupStatusCode.InProgress
      ) {
        return 5000; // Refetch every 5 seconds for active jobs
      }
      return false; // Stop refetching for completed/failed jobs
    },
    staleTime: 1000 * 60, // Consider data stale after 1 minute
  });

  // Clear cache when lookup_status changes to completed
  useEffect(() => {
    const currentStatus = result.data?.lookup_status;
    const previousStatus = previousStatusRef.current;

    // Refresh caches when lookup_status changes to complete
    if (
      currentStatus === SavedSearchLookupStatusCode.Complete &&
      !isNullOrUndefined(previousStatus) &&
      previousStatus !== SavedSearchLookupStatusCode.Complete
    ) {
      // Invalidate all saved search related queries
      queryClient.invalidateQueries({
        queryKey: getNsSavedSearchCacheKey(savedSearchId),
      });
      queryClient.invalidateQueries({
        queryKey: ["listingDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: getNsSavedSearchDetailsCacheKey(savedSearchId),
      });
      queryClient.invalidateQueries({
        queryKey: [NS_LIST_SAVED_SEARCHES_CACHE_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [NS_INFINITE_LIST_SAVED_SEARCHES_CACHE_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: ["searchListings", EnumProductTypes.ns],
      });
    }

    previousStatusRef.current = currentStatus;
  }, [result.data?.lookup_status, savedSearchId, queryClient]);

  return result;
};
